import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Grid,
  Paper,
  InputAdornment,
  Slider,
  FormHelperText,
} from '@mui/material';

// Icons
import ShowChartIcon from '@mui/icons-material/ShowChart';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import SentimentSatisfiedIcon from '@mui/icons-material/SentimentSatisfied';
import SentimentDissatisfiedIcon from '@mui/icons-material/SentimentDissatisfied';
import SentimentNeutralIcon from '@mui/icons-material/SentimentNeutral';

// Custom components
import StockSearchBar from '../../../common/StockSearchBar';

const NewsAlertConfig = ({ alertData, onUpdateAlertData }) => {
  const [keywordInput, setKeywordInput] = useState('');

  const mainFilters = alertData.mainFilters || [];
  const conditions = alertData.conditions || {};

  const symbols = mainFilters
    .filter((filter) => filter.type === 'symbol')
    .map((filter) => filter.value);

  const keywords = mainFilters
    .filter((filter) => filter.type === 'keyword')
    .map((filter) => filter.value);

  const handleAddSymbol = (symbol) => {
    if (!symbol || symbols.includes(symbol)) return;

    const updatedMainFilters = [
      ...mainFilters,
      { type: 'symbol', value: symbol },
    ];

    onUpdateAlertData({
      mainFilters: updatedMainFilters,
    });
  };

  const handleAddKeyword = (e) => {
    e.preventDefault();
    if (!keywordInput.trim() || keywords.includes(keywordInput.trim())) {
      setKeywordInput('');
      return;
    }

    const updatedMainFilters = [
      ...mainFilters,
      { type: 'keyword', value: keywordInput.trim() },
    ];

    onUpdateAlertData({
      mainFilters: updatedMainFilters,
    });

    setKeywordInput('');
  };

  const handleRemoveSymbol = (symbolToRemove) => {
    const updatedMainFilters = mainFilters.filter(
      (filter) => !(filter.type === 'symbol' && filter.value === symbolToRemove)
    );

    onUpdateAlertData({
      mainFilters: updatedMainFilters,
    });
  };

  const handleRemoveKeyword = (keywordToRemove) => {
    const updatedMainFilters = mainFilters.filter(
      (filter) => !(filter.type === 'keyword' && filter.value === keywordToRemove)
    );

    onUpdateAlertData({
      mainFilters: updatedMainFilters,
    });
  };

  const handleSentimentChange = (e) => {
    onUpdateAlertData({
      conditions: {
        ...conditions,
        sentiment: e.target.value,
      },
    });
  };

  const handleSentimentScoreChange = (e, newValue) => {
    onUpdateAlertData({
      conditions: {
        ...conditions,
        sentimentScore: newValue,
      },
    });
  };

  const handleSentimentComparisonChange = (e) => {
    onUpdateAlertData({
      conditions: {
        ...conditions,
        sentimentComparison: e.target.value,
      },
    });
  };

  return (
    <Box sx={{ py: 2 }}>
      <Typography variant="h6" gutterBottom>
        1. Select Stocks to Monitor (Optional)
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 4 }}>
        <Box sx={{ mb: 2 }}>
          <StockSearchBar
            onSymbolSelect={(symbol, resultData) => handleAddSymbol(symbol)}
            currentSymbols={symbols}
            maxSymbols={Infinity}
            placeholder="Search for a stock by symbol or name"
            disabled={false}
          />
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {symbols.length > 0 ? (
            symbols.map((symbol) => (
              <Chip
                key={symbol}
                label={symbol}
                onDelete={() => handleRemoveSymbol(symbol)}
                color="primary"
                variant="outlined"
                icon={<ShowChartIcon />}
              />
            ))
          ) : (
            <Typography variant="body2" color="text.secondary">
              No stocks selected. If you add stocks, you'll only be notified about news for these specific stocks.
            </Typography>
          )}
        </Box>
      </Paper>

      <Typography variant="h6" gutterBottom>
        2. Add Keywords to Track (Optional)
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 4 }}>
        <Box sx={{ mb: 2 }} component="form" onSubmit={handleAddKeyword}>
          <TextField
            label="Add keyword"
            variant="outlined"
            value={keywordInput}
            onChange={(e) => setKeywordInput(e.target.value)}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <KeyboardIcon />
                </InputAdornment>
              ),
            }}
            helperText="Press Enter to add each keyword (e.g., 'acquisition', 'earnings')"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddKeyword(e);
              }
            }}
          />
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {keywords.length > 0 ? (
            keywords.map((keyword) => (
              <Chip
                key={keyword}
                label={keyword}
                onDelete={() => handleRemoveKeyword(keyword)}
                color="secondary"
                variant="outlined"
                icon={<KeyboardIcon />}
              />
            ))
          ) : (
            <Typography variant="body2" color="text.secondary">
              No keywords added. Add keywords to get alerts when news articles contain these terms.
            </Typography>
          )}
        </Box>
      </Paper>

      <Typography variant="h6" gutterBottom>
        3. Filter by Sentiment (Optional)
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 4 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel id="sentiment-label">News Sentiment</InputLabel>
              <Select
                labelId="sentiment-label"
                value={conditions.sentiment || ''}
                onChange={handleSentimentChange}
                label="News Sentiment"
              >
                <MenuItem value="">Any Sentiment</MenuItem>
                <MenuItem value="positive">
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <SentimentSatisfiedIcon sx={{ mr: 1, color: 'positive.main' }} />
                    Positive
                  </Box>
                </MenuItem>
                <MenuItem value="negative">
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <SentimentDissatisfiedIcon sx={{ mr: 1, color: 'negative.main' }} />
                    Negative
                  </Box>
                </MenuItem>
                <MenuItem value="neutral">
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <SentimentNeutralIcon sx={{ mr: 1 }} />
                    Neutral
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel id="sentiment-comparison-label">Sentiment Score</InputLabel>
              <Select
                labelId="sentiment-comparison-label"
                value={conditions.sentimentComparison || ''}
                onChange={handleSentimentComparisonChange}
                label="Sentiment Score"
              >
                <MenuItem value="">Any Score</MenuItem>
                <MenuItem value="greater">Greater Than</MenuItem>
                <MenuItem value="less">Less Than</MenuItem>
              </Select>
              {conditions.sentimentComparison && (
                <Box sx={{ mt: 2, px: 1 }}>
                  <Typography id="sentiment-score-slider" gutterBottom>
                    Sentiment Score: {conditions.sentimentScore || 0}
                  </Typography>
                  <Slider
                    value={conditions.sentimentScore || 0}
                    onChange={handleSentimentScoreChange}
                    min={-1}
                    max={1}
                    step={0.1}
                    marks={[
                      { value: -1, label: 'Negative' },
                      { value: 0, label: 'Neutral' },
                      { value: 1, label: 'Positive' },
                    ]}
                    aria-labelledby="sentiment-score-slider"
                  />
                  <FormHelperText>
                    {conditions.sentimentComparison === 'greater'
                      ? 'Alert when sentiment is more positive than this score'
                      : 'Alert when sentiment is more negative than this score'}
                  </FormHelperText>
                </Box>
              )}
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {symbols.length === 0 && keywords.length === 0 && (
        <Typography variant="body2" color="error" sx={{ mt: 2 }}>
          Please select at least one stock or add at least one keyword to monitor.
        </Typography>
      )}
    </Box>
  );
};

export default NewsAlertConfig;
